import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useNotice } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/NoticeHost';
import { refusalProse } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/refusalProse';
import {
  BATCH_LANDED,
  isLandedUnreadable,
  useBatchMoveSeatsMutation,
  useMoveSeatMutation,
  useRevokeSeatMutation,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

import {
  buildReport,
  cohortsRemoved,
  landedCount,
  landedOverCap,
  type ReportRow,
} from './batchReport';
import { CohortMembershipPalette } from './CohortMembershipPalette';
import { seatContextBySeatId, holdersWithoutEligibleSeat, movableSeats } from './memberSeats';
import { MoveSeatsDialogView } from './MoveSeatsDialogView';
import { RevokeSeatDialogView } from './RevokeSeatDialogView';
import { useAgentContext } from './seatActions.agentContext';
import type { SeatAction } from './useSeatActions';

const { seatActions } = appStrings.modules.coachingDashboard;

type SeatActionDialogsProps = Readonly<{
  action: SeatAction | null;
  onClose: () => void;
  /** Runs once a write lands, so a selection the write consumed is dropped. */
  onLanded: () => void;
}>;

export const SeatActionDialogs = ({ action, onClose, onLanded }: SeatActionDialogsProps) => {
  const notice = useNotice();

  useAgentContext(action);
  const [moveSeat, moveSeatState] = useMoveSeatMutation();
  const [batchMoveSeats, batchMoveState] = useBatchMoveSeatsMutation();
  const [revokeSeat, revokeSeatState] = useRevokeSeatMutation();

  const holders = action?.holders ?? [];

  const announce = (message: string) => {
    notice.announce(message);
    onLanded();
  };

  const onMove: React.ComponentProps<typeof MoveSeatsDialogView>['doors']['onMove'] = async ({
    seatIds,
    sourceRosterId,
    destinationRosterId,
    destinationName,
    sourceGrantId,
  }) => {
    try {
      if (sourceRosterId !== null && holders.length === 1 && seatIds.length === 1) {
        const moved = await moveSeat({
          rosterId: sourceRosterId,
          seatId: seatIds[0],
          destinationRosterId,
          sourceGrantId,
        }).unwrap();

        announce(
          moved.isOverCap
            ? seatActions.moveDialog.movedOverCap(holders[0].name, destinationName)
            : seatActions.moveDialog.moved(holders[0].name, destinationName),
        );

        // The banner above already carried the cap warning for a single move, and this
        // door renders no report to put it on.
        return {
          ok: true,
          message: null,
          rows: [],
          isOverCap: false,
          cohortsRemoved: moved.revokedCohortMemberships,
        };
      }

      const results = await batchMoveSeats({
        seatIds,
        destinationRosterId,
        sourceGrantId,
      }).unwrap();
      const items = results.map((result) => ({
        id: result.seatId,
        outcome: result.outcome,
        isOverCap: result.isOverCap,
        revokedCohortMemberships: result.revokedCohortMemberships,
      }));
      // One word decides both what the count calls a landing and which rows the cap
      // warning is read off. Named once so the two cannot disagree about it.
      const landedWord = BATCH_LANDED.move;
      const rows = buildReport(
        items,
        seatContextBySeatId(holders),
        landedWord,
        holdersWithoutEligibleSeat(holders, movableSeats),
      );

      if (landedCount(rows) > 0) onLanded();

      return {
        ok: true,
        message: null,
        rows,
        isOverCap: landedOverCap(items, landedWord),
        cohortsRemoved: cohortsRemoved(items),
      };
    } catch (error) {
      // Past a 2xx the seats have already moved; only the report was lost. Reporting it
      // as a refusal would invite a retry that refuses again for a different reason.
      if (isLandedUnreadable(error)) {
        announce(
          holders.length === 1
            ? seatActions.landedUnreadable.move(holders[0].name, destinationName)
            : seatActions.landedUnreadable.batch,
        );

        return {
          ok: true,
          message: null,
          rows: [] as ReportRow[],
          isOverCap: false,
          cohortsRemoved: null,
        };
      }

      return {
        ok: false,
        message: refusalProse(error),
        rows: [] as ReportRow[],
        isOverCap: false,
        cohortsRemoved: null,
      };
    }
  };

  const onRevoke: React.ComponentProps<typeof RevokeSeatDialogView>['doors']['onRevoke'] = async ({
    rosterId,
    seatId,
    name,
  }) => {
    try {
      await revokeSeat({ rosterId, seatId }).unwrap();
      announce(seatActions.revokeDialog.revoked(name));

      return { ok: true, message: null };
    } catch (error) {
      if (isLandedUnreadable(error)) {
        announce(seatActions.landedUnreadable.revoke(name));

        return { ok: true, message: null };
      }

      return { ok: false, message: refusalProse(error) };
    }
  };

  return (
    <>
      <MoveSeatsDialogView
        open={action?.kind === 'move'}
        onClose={onClose}
        holders={holders}
        doors={{ isMoving: moveSeatState.isLoading || batchMoveState.isLoading, onMove }}
      />
      {action?.kind === 'revoke' && holders.length === 1 ? (
        <RevokeSeatDialogView
          open
          onClose={onClose}
          holder={holders[0]}
          isCoachSeat={action.isCoachSeat}
          doors={{ isRevoking: revokeSeatState.isLoading, onRevoke }}
        />
      ) : null}
      {action?.kind === 'addCohort' || action?.kind === 'removeCohort' ? (
        <CohortMembershipPalette
          open
          onClose={onClose}
          mode={action.kind === 'addCohort' ? 'add' : 'remove'}
          holder={holders[0]}
        />
      ) : null}
    </>
  );
};
